/**
 * Plans and what each one opens (MENIU_PLANAS.md §2, decided 2026-10-08). Add-ons are not sold one by one:
 * a section is in Basic or in Premium. The plans that include our accountant's work (Pilna apskaita) or are
 * for accounting firms (Firm) have Premium's sections. Shared by the API (which enforces it) and the panel
 * (which shows locks and the upgrade page).
 */
export const PLAN_IDS = ['free', 'basic', 'premium', 'full', 'firm', 'iv'];
export const LEVEL_RANK = { free: 0, basic: 1, premium: 2 };
/** Individuali veikla (a person working on their own) has Basic's sections and its own journal. */
export const PLAN_LEVEL = { free: 'free', basic: 'basic', premium: 'premium', full: 'premium', firm: 'premium', iv: 'basic' };
export const atLeast = (level, needed) => LEVEL_RANK[level] >= LEVEL_RANK[needed];
/** Plans bought by card in the panel, with list prices before VAT (cents); a year is ten months. */
export const SELF_SERVE = ['basic', 'premium', 'iv'];
export const PLAN_PRICES = {
    basic: { month: 600, year: 6000 },
    premium: { month: 1900, year: 19000 },
    iv: { month: 300, year: 2900 },
};
/**
 * Firm (accounting firms): per client company a month, in steps — the first 20 clients at 30 €, the next 30 at
 * 27 €, every one above 50 at 24 € (so each new client adds to the total) — at least five clients billed. A year
 * is ten months, as for the other plans.
 */
export const FIRM_TIERS = [{ upTo: 20, cents: 3000 }, { upTo: 50, cents: 2700 }, { upTo: null, cents: 2400 }];
export const FIRM_MIN_CLIENTS = 5;
/** Clients billed: the active ones, at least the minimum. */
export const firmUnits = (clients) => Math.max(FIRM_MIN_CLIENTS, clients);
/** What a firm with this many clients pays a month (or a year: ten months). */
export function firmPrice(clients, interval = 'month') {
    let left = firmUnits(clients), from = 0, total = 0;
    for (const t of FIRM_TIERS) {
        const n = t.upTo === null ? left : Math.min(left, t.upTo - from);
        total += n * t.cents;
        left -= n;
        from = t.upTo ?? from;
        if (!left)
            break;
    }
    return total * (interval === 'year' ? 10 : 1);
}
export const BUYABLE = [...SELF_SERVE, 'firm'];
/** Pilna apskaita: from this much a month, with Premium; the rest by agreement (VAT payer, documents, employees). */
export const FULL_FROM_CENTS = 5900;
/** Nemokamas: outgoing invoices a month. */
export const FREE_INVOICES_A_MONTH = 5;
/** People in the company: those who work in it, and read-only viewers (e.g. an outside accountant) on top. */
export const USER_LIMITS = {
    free: { users: 1, viewers: 1 },
    basic: { users: 1, viewers: 1 },
    premium: { users: 5, viewers: 0 },
};
/** Panel sections and the level they need; everything else (Sąskaitos, Klientai, Tiekėjai, Įmonė…) is in every plan. */
export const SECTION_LEVEL = {
    '/quotes': 'basic', '/recurring': 'basic', '/agreements': 'basic', '/payments': 'basic', '/projects': 'basic',
    '/tasks': 'basic', '/calendar': 'basic', '/notes': 'basic', '/files': 'basic', '/iv': 'basic',
    '/assistant': 'premium', '/reconciliation': 'premium', '/bank': 'premium', '/cash': 'premium', '/taxes': 'premium',
    '/payroll': 'premium', '/close': 'premium', '/accountant': 'premium', '/export': 'premium', '/accounts': 'premium', '/journal': 'premium', '/books': 'premium', '/assets': 'premium', '/dividends': 'premium', '/declarations': 'premium', '/fixed-assets': 'premium',
};
/**
 * The API behind those sections: changes need the level (reading stays open, so nothing a company made
 * disappears when the plan goes down). Longest prefix first where they overlap.
 */
export const API_LEVEL = [
    // A document photographed in the phone app: Basic too, a few a month (SCANS_A_MONTH), and turning it into a received
    // invoice or setting it aside; the rest of the inbox (its e-mail address, senders) is Premium's.
    ['/api/v1/inbox/upload', 'basic'], ['/api/v1/inbox/:id/open', 'basic'], ['/api/v1/inbox/:id/dismiss', 'basic'], ['/api/v1/inbox/:id/move', 'basic'],
    ['/api/v1/quotes', 'basic'], ['/api/v1/recurring', 'basic'], ['/api/v1/agreements', 'basic'], ['/api/v1/payments', 'basic'],
    ['/api/v1/projects', 'basic'], ['/api/v1/tasks', 'basic'], ['/api/v1/task-', 'basic'], ['/api/v1/calendar', 'basic'],
    ['/api/v1/notes', 'basic'], ['/api/v1/drive', 'basic'], ['/api/v1/reminders', 'basic'], ['/api/v1/iv', 'basic'],
    ['/api/v1/ai/chat', 'premium'], ['/api/v1/reconciliation', 'premium'], ['/api/v1/partners', 'premium'], ['/api/v1/bank', 'premium'],
    ['/api/v1/taxes', 'premium'], ['/api/v1/employees', 'premium'], ['/api/v1/payroll', 'premium'], ['/api/v1/close', 'premium'],
    ['/api/v1/accountant-queue', 'premium'], ['/api/v1/export', 'premium'], ['/api/v1/accounts', 'premium'], ['/api/v1/journal', 'premium'], ['/api/v1/books', 'premium'], ['/api/v1/assets', 'premium'], ['/api/v1/dividends', 'premium'], ['/api/v1/inbox', 'premium'],
    // The till, except a cash receipt (KPO) for an invoice: in every plan an issued invoice is paid by bank or KPO.
    ['/api/v1/cash', 'premium'],
];
/** Documents a month a company may photograph and have read in the phone app, below Premium (no limit there). */
export const SCANS_A_MONTH = { basic: 20 };
/** The level an API change needs, or null when every plan may do it. */
export function apiLevel(method, path, body) {
    if (method === 'GET' || method === 'HEAD')
        return null;
    if (path === '/api/v1/cash/entries' && method === 'POST' && typeof body === 'object' && body !== null && 'invoiceId' in body && body.invoiceId)
        return null;
    // The receipt's scan, or taking a receipt back (only receipts for invoices exist below Premium).
    if (path === '/api/v1/cash/entries/:id/file' || (path === '/api/v1/cash/entries/:id' && method === 'DELETE'))
        return null;
    return API_LEVEL.find(([prefix]) => path.startsWith(prefix))?.[1] ?? null;
}
/** Sections the level does not open (the menu shows them locked). */
export const lockedSections = (level) => Object.entries(SECTION_LEVEL).filter(([, needed]) => !atLeast(level, needed)).map(([path]) => path);
//# sourceMappingURL=plans.js.map